import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Receipt, Search, Users, Wallet, X } from 'lucide-react';
import {
  searchGlobal,
  type SearchResult,
  type SearchResultKind,
  type GlobalSearchPayload,
} from '@/services/api/search';
import { routes } from '@/lib/routes';
import { cn } from '@/lib/cn';

interface Props {
  open: boolean;
  onClose: () => void;
  empresaId: string;
}

const EMPTY_PAYLOAD: GlobalSearchPayload = {
  administraciones: [],
  edificios: [],
  comprobantes: [],
  movimientos: [],
};

/**
 * Modal de búsqueda global. Se abre con Cmd+K (macOS) o Ctrl+K (Windows/Linux)
 * desde cualquier parte de la app, o con click en el icono de búsqueda del
 * topbar. Busca en paralelo admins, edificios, comprobantes y movimientos.
 *
 * Navegación por teclado:
 *   ↑ ↓     → mover entre resultados (cross-category)
 *   Enter  → navegar al seleccionado
 *   Esc    → cerrar
 *   Tab    → ir al input
 */
export function GlobalSearch({ open, onClose, empresaId }: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [payload, setPayload] = useState<GlobalSearchPayload>(EMPTY_PAYLOAD);
  const [loading, setLoading] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Lista plana en el orden de navegación (mismo que el visual).
  const flatResults = useMemo(() => {
    return [
      ...payload.administraciones,
      ...payload.edificios,
      ...payload.comprobantes,
      ...payload.movimientos,
    ];
  }, [payload]);

  // Reset al abrir.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setPayload(EMPTY_PAYLOAD);
    setHoveredIdx(0);
    const t = setTimeout(() => inputRef.current?.focus(), 20);
    return () => clearTimeout(t);
  }, [open]);

  // Debounced search. 200ms es suficiente para que no dispare en cada tecla.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) {
      setPayload(EMPTY_PAYLOAD);
      setLoading(false);
      return;
    }
    setLoading(true);
    let cancel = false;
    const t = setTimeout(() => {
      searchGlobal(empresaId, q).then((res) => {
        if (cancel) return;
        setLoading(false);
        if (res.ok) {
          setPayload(res.data);
          setHoveredIdx(0);
        }
      });
    }, 200);
    return () => {
      cancel = true;
      clearTimeout(t);
    };
  }, [query, empresaId, open]);

  const goTo = useCallback(
    (r: SearchResult) => {
      const url = urlFor(empresaId, r);
      if (url) navigate(url);
      onClose();
    },
    [empresaId, navigate, onClose],
  );

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHoveredIdx((i) => Math.min(flatResults.length - 1, i + 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHoveredIdx((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const r = flatResults[hoveredIdx];
      if (r) goTo(r);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center bg-black/40 p-4 pt-[12vh]"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl overflow-hidden rounded-xl border border-surface-line bg-white shadow-premium"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 border-b border-surface-line px-4 py-3">
          <Search size={18} className="text-neutral-muted" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder="Buscar administraciones, edificios, comprobantes, movimientos…"
            className="flex-1 border-none bg-transparent text-base text-neutral-ink outline-none placeholder:text-neutral-muted/70"
          />
          {loading && (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue border-t-transparent" />
          )}
          <button
            type="button"
            onClick={onClose}
            className="grid h-6 w-6 place-items-center rounded text-neutral-muted transition hover:bg-surface-bg hover:text-neutral-ink"
            aria-label="Cerrar"
          >
            <X size={14} />
          </button>
        </div>

        {/* Resultados */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto">
          {query.trim().length < 2 ? (
            <div className="px-6 py-10 text-center text-sm text-neutral-muted">
              <p>Escribí al menos 2 caracteres.</p>
              <p className="mt-2 text-[11px]">
                Tip: los comprobantes se buscan por número (sólo dígitos) o por razón
                social. Los movimientos por descripción o referencia.
              </p>
            </div>
          ) : flatResults.length === 0 && !loading ? (
            <div className="px-6 py-10 text-center text-sm text-neutral-muted">
              No encontré nada que matchee con{' '}
              <b className="text-neutral-ink">"{query}"</b>.
            </div>
          ) : (
            <div className="divide-y divide-surface-line/60">
              <ResultGroup
                title="Administraciones"
                icon={Users}
                results={payload.administraciones}
                offset={0}
                hoveredIdx={hoveredIdx}
                setHoveredIdx={setHoveredIdx}
                goTo={goTo}
              />
              <ResultGroup
                title="Edificios"
                icon={Building2}
                results={payload.edificios}
                offset={payload.administraciones.length}
                hoveredIdx={hoveredIdx}
                setHoveredIdx={setHoveredIdx}
                goTo={goTo}
              />
              <ResultGroup
                title="Comprobantes"
                icon={Receipt}
                results={payload.comprobantes}
                offset={payload.administraciones.length + payload.edificios.length}
                hoveredIdx={hoveredIdx}
                setHoveredIdx={setHoveredIdx}
                goTo={goTo}
              />
              <ResultGroup
                title="Movimientos"
                icon={Wallet}
                results={payload.movimientos}
                offset={
                  payload.administraciones.length +
                  payload.edificios.length +
                  payload.comprobantes.length
                }
                hoveredIdx={hoveredIdx}
                setHoveredIdx={setHoveredIdx}
                goTo={goTo}
              />
            </div>
          )}
        </div>

        {/* Footer con atajos */}
        <div className="flex items-center justify-between gap-3 border-t border-surface-line/60 bg-surface-bg/40 px-4 py-2 text-[11px] text-neutral-muted">
          <div className="flex items-center gap-3">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            <span>navegar</span>
            <Kbd>↵</Kbd>
            <span>abrir</span>
            <Kbd>Esc</Kbd>
            <span>cerrar</span>
          </div>
          <span>
            <Kbd>⌘</Kbd>
            <Kbd>K</Kbd>
            <span className="ml-1">para abrir</span>
          </span>
        </div>
      </div>
    </div>
  );
}

function ResultGroup({
  title,
  icon: Icon,
  results,
  offset,
  hoveredIdx,
  setHoveredIdx,
  goTo,
}: {
  title: string;
  icon: typeof Search;
  results: SearchResult[];
  offset: number;
  hoveredIdx: number;
  setHoveredIdx: (i: number) => void;
  goTo: (r: SearchResult) => void;
}) {
  if (results.length === 0) return null;
  return (
    <div className="py-1.5">
      <p className="px-4 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-muted">
        {title}
      </p>
      {results.map((r, i) => {
        const globalIdx = offset + i;
        const isHovered = globalIdx === hoveredIdx;
        return (
          <button
            key={`${r.kind}-${r.id}`}
            type="button"
            onClick={() => goTo(r)}
            onMouseEnter={() => setHoveredIdx(globalIdx)}
            className={cn(
              'flex w-full items-start gap-3 px-4 py-2 text-left transition',
              isHovered ? 'bg-blue-10/60' : 'hover:bg-blue-10/30',
            )}
          >
            <Icon
              size={15}
              className={cn(
                'mt-0.5 flex-none',
                isHovered ? 'text-blue' : 'text-neutral-muted',
              )}
            />
            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  'truncate text-sm font-semibold',
                  isHovered ? 'text-blue' : 'text-neutral-ink',
                )}
              >
                {r.label}
              </p>
              {r.sublabel && (
                <p className="truncate text-[11px] text-neutral-muted">{r.sublabel}</p>
              )}
            </div>
            {r.extra && (
              <p className="flex-none whitespace-nowrap text-[11px] text-neutral-muted">
                {r.extra}
              </p>
            )}
          </button>
        );
      })}
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex h-5 min-w-[20px] items-center justify-center rounded border border-surface-line bg-white px-1.5 font-mono text-[10px] font-semibold text-neutral-ink shadow-sm">
      {children}
    </kbd>
  );
}

/**
 * Mapea cada resultado a su URL. Admins y edificios llevan a la grilla
 * correspondiente con filtro preseteado; comprobantes a Facturación
 * filtrada por ese admin; movimientos a Finanzas filtrada por su caja.
 */
function urlFor(empresaId: string, r: SearchResult): string | null {
  switch (r.kind as SearchResultKind) {
    case 'administracion':
      // Abrimos la grilla de admins y linkeamos con un query param para
      // que abra el drawer del admin. Si el usuario prefiere, a futuro
      // podría ser la cta cte pre-filtrada.
      return `${routes.empresa.clientes.administraciones(empresaId)}?admin=${r.id}`;
    case 'edificio':
      return `${routes.empresa.clientes.edificios(empresaId)}?edif=${r.id}`;
    case 'comprobante':
      // El detalle vive en Facturación (no hay ruta /comprobantes/:id aún).
      // Lo linkeamos a la grilla con el filtro por comprobante.
      return `${routes.empresa.facturacion(empresaId)}?comprobante=${r.id}`;
    case 'movimiento':
      return `${routes.empresa.finanzas(empresaId)}?mov=${r.id}`;
    default:
      return null;
  }
}
